import {
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  ToggleButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import {
  setFfd,
  setLfd,
  setZone,
  setClimateDataFromHex,
  setClimateDataFromInterpolated,
  useAddFarmContext,
  useAddFarmContextDispatch,
} from './context/add-farm-context'
import { useGlobalClimate } from './use-global-climate'
import { calculateAgriZone, getCropRecommendations } from './zone-calculator'
import { FrostDateEditor } from './frost-date-editor'
import { supabaseGetter } from '~/api/supabase-getter'
import { MapContainer, Marker, Polygon, TileLayer } from 'react-leaflet'
import { DESKTOP_BREAKPOINT } from '~/common/constants/layout-constants'
import { Era5DisclaimerTooltip } from './era5-disclaimer-tooltip'
import { TpToggleButtonGroup, TpTooltip } from '~/components'
import L from 'leaflet'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import { getWrappedBoundary } from './coordinate-utils'
import { useTemperatureUnit } from '~/common/use-temperature-unit'

// Preload marker icon for speedy loading
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

const HEX_COUNT = 7
const DEFAULT_ZOOM = 7.75

export const AddFarmModalPage2 = ({ header, onBack, onSubmit }) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_BREAKPOINT))
  const [selectedHex, setSelectedHex] = useState(null)
  const [useInterpolation, setUseInterpolation] = useState(true)
  const [interpolatedData, setInterpolatedData] = useState(null)
  const [climateStats, setClimateStats] = useState(null)
  const { lat, lng, name, gdd10, gdd5, stdLfd, stdFfd, lfd, ffd } = useAddFarmContext()
  const dispatch = useAddFarmContextDispatch()
  const { fetchNearest, climateHexes, loading } = useGlobalClimate()
  const { displayThreshold } = useTemperatureUnit()

  const [isManualOverride, setIsManualOverride] = useState(false)

  const isLoading = loading // || !interpolatedData // || !climateStats

  const handleHexClick = useCallback(({ hex }) => {
    setSelectedHex(hex)
    const agriZone = calculateAgriZone(hex)
    setClimateStats(agriZone)
    dispatch(setZone(agriZone.usdaZone))
    dispatch(setClimateDataFromHex(hex))
  }, [dispatch])

  // Frost date editor callbacks
  const handleLfdChange = useCallback((doy) => {
    dispatch(setLfd(doy))
  }, [dispatch])

  const handleFfdChange = useCallback((doy) => {
    dispatch(setFfd(doy))
  }, [dispatch])

  // FrostDateEditor handles re-dispatching the percentile-adjusted lfd/ffd on reset;
  // this callback only needs to exist as a no-op to satisfy the prop contract.
  const handleResetDates = useCallback(() => {}, [])

  useEffect(() => {
    if (!lat || !lng) return

    const loadClimateData = async () => {
      if (useInterpolation) {
        // Fetch nearest hexes for map display (populates climateHexes array)
        await fetchNearest(lat, lng, HEX_COUNT)

        // Fetch interpolated climate data (DEFAULT MODE)
        const { data: interpolated, error } =
          await supabaseGetter.globalClimateAtlas.getInterpolated(lat, lng, 7)

        if (interpolated && !error) {
          setInterpolatedData(interpolated)
          const agriZone = calculateAgriZone({
            avg_gdd10: interpolated.interpolated_gdd10,
            abs_min_temp: interpolated.interpolated_abs_min_temp,
            avg_ffw: interpolated.interpolated_avg_ffw,
          })
          setClimateStats(agriZone)
          dispatch(setZone(agriZone.usdaZone))
          dispatch(setClimateDataFromInterpolated(interpolated))
        }
      } else {
        // Manual hex selection mode - fetchNearest populates map AND returns closest hex
        const hex = await fetchNearest(lat, lng, HEX_COUNT)
        if (hex) handleHexClick({ hex })
      }
    }

    loadClimateData()
  }, [lat, lng, useInterpolation, fetchNearest, handleHexClick, dispatch])

  const _onBack = () => {
    // No need to clear state - context retains values for navigation
    onBack()
  }

  const _onSubmit = async () => {
    onSubmit()
  }

  return (
    <Box
      component="form"
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90vw', md: '80vw', lg: '70vw', xl: '60vw' },
        bgcolor: 'background.paper',
        borderRadius: '0.5rem',
        boxShadow: 24,
        p: '1rem 2rem',
      }}
    >
      {header}
      <Era5DisclaimerTooltip />

      {/* Instructions */}
      <Typography variant="h6" sx={{ py: '0.5rem' }}>
        Select Climate Zone
      </Typography>

      {isLoading && <LinearProgress />}
    
      <Stack>
        <Stack
          direction={isDesktop ? 'row' : 'column'}
          spacing="0.5rem"
          sx={
            !isDesktop
              ? { alignItems: 'center', justifyContent: 'center' }
              : undefined
          }
        >
          {/* Map Container - matches page 1 dimensions and layout */}
          <Box
            sx={{
              width: '100%',
              aspectRatio: '16 / 9',
              overflow: 'hidden',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {interpolatedData && (
              <MapContainer
                center={[lat, lng]}
                zoom={DEFAULT_ZOOM}
                zoomSnap={0.25}
                zoomDelta={0.25}
                zoomControl={false}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  maxZoom={19}
                />

                {/* Farm location marker */}
                {/* override alt since it renders by default until the marker icon appears */}
                <Marker position={[lat, lng]} alt=''/>

                {/* Interpolation Mode Toggle */}
                <Stack sx={{ zIndex: 400, position: 'absolute', alignItems: 'center', bottom: 10, left: 10 }}>
                  <TpToggleButtonGroup
                    value={useInterpolation ? 'interpolate' : 'selection'}
                    exclusive
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        const newInterpolation = newValue === 'interpolate'
                        setUseInterpolation(newInterpolation)
                        if (newInterpolation) setSelectedHex(null)
                      }
                    }}
                    size="small"
                    sx={{
                      bgcolor: 'background.paper',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}
                  >
                    <ToggleButton value="interpolate">
                      Interpolate from all
                    </ToggleButton>
                    <ToggleButton value="selection">
                      Single selection
                    </ToggleButton>
                  </TpToggleButtonGroup>
                </Stack>

                {/* Climate hexagons */}
                {climateHexes.map((hex) => {
                  const isSelected = selectedHex?.h3_index === hex.h3_index

                  // FIX FOR ANTI-MERIDIAN (DATE LINE)
                  const wrappedBoundary = getWrappedBoundary(hex.h3_index)

                  const hexMetadata = interpolatedData?.hexes_used?.find(
                    h => h.h3_index === hex.h3_index
                  )
                  const isContributing = useInterpolation && hexMetadata

                  // Calculate heatmap color intensity based on weight (0-1)
                  const getHeatmapColor = (weight) => {
                    // Map weight to green intensity (higher weight = more saturated green)
                    const intensity = Math.round(weight * 255)
                    return `rgb(16, ${intensity + 100}, 129)` // Green with varying intensity
                  }

                  // When manually overridden, show all hexes as greyscale
                  const hexColor = isManualOverride
                    ? '#888888'
                    : (isSelected
                        ? climateStats?.color
                        : (isContributing
                            ? getHeatmapColor(hexMetadata.weight)
                            : '#3b82f6'
                          )
                      )

                  const hexOpacity = isManualOverride
                    ? 0.3
                    : (isSelected ? 0.6 : (isContributing ? 0.5 : 0.2))

                  return (
                    <Polygon
                      key={hex.h3_index}
                      positions={wrappedBoundary}
                      pathOptions={{
                        fillColor: hexColor,
                        fillOpacity: hexOpacity,
                        weight: isSelected ? 2 : (useInterpolation ? 0 : 1),
                        color: isSelected ? climateStats?.color : (useInterpolation ? 'transparent' : '')
                      }}
                      eventHandlers={{
                        click: () => !useInterpolation && handleHexClick({ hex })
                      }}
                    />
                  )
                })}
              </MapContainer>
            )}
          </Box>

          {/* Climate Stats Display */}
          {climateStats && (
            <Stack spacing="0.5rem" width='100%'>
              {/* Frost Date Editor */}
              <FrostDateEditor
                key={useInterpolation ? 'interpolate' : (selectedHex?.h3_index || 'none')}
                defaultLfd={useInterpolation
                  ? interpolatedData?.interpolated_lfd
                  : selectedHex?.avg_lfd
                }
                defaultFfd={useInterpolation
                  ? interpolatedData?.interpolated_ffd
                  : selectedHex?.avg_ffd
                }
                stdLfd={stdLfd}
                stdFfd={stdFfd}
                onLfdChange={handleLfdChange}
                onFfdChange={handleFfdChange}
                onReset={handleResetDates}
                onModifiedChange={setIsManualOverride}
              />

              {/* Climate Snapshot */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  width: '100%'
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    USDA Zone {climateStats.usdaZone} | {climateStats.label}
                  </Typography>
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                  <Box>
                    <TpTooltip
                      title="Days between last spring frost and first fall frost. Determines your growing season length. Year-round climates have 300+ days."
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                        Frost-Free Window*
                      </Typography>
                    </TpTooltip>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {lfd && ffd ? ffd - lfd : climateStats.frostFreeDays} Days
                    </Typography>
                    {isManualOverride ? (
                      <Chip
                        label="Manual"
                        size="small"
                        color="warning"
                        sx={{ height: 18, fontSize: '0.65rem', mt: 0.5 }}
                      />
                    ) : (
                      <Chip
                        label={useInterpolation ? "Interpolated" : "From Selection"}
                        size="small"
                        color="info"
                        sx={{ height: 18, fontSize: '0.65rem', mt: 0.5 }}
                      />
                    )}
                  </Box>
                  <Box>
                    <TpTooltip
                      title="Heat accumulation during the growing season with respect to a base temperature. Tropical climates can reach 4000+ GDD."
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                        Growing Degree Days*
                      </Typography>
                    </TpTooltip>
                    <Stack direction='row' spacing={1.5} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Stack direction='row' spacing={0.5} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {gdd10}
                        </Typography>
                        <Typography variant="body1" sx={{ textAlign: 'center', fontSize: '0.75rem', fontWeight: 'light', lineHeight: 1.3 }}>
                          Base {displayThreshold(10)}
                        </Typography>
                      </Stack>

                      <Divider orientation='vertical' flexItem sx={{ borderColor: 'grey.400', alignSelf: 'auto', minHeight: 16 }} />
                      
                      <Stack direction='row' spacing={0.5} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {gdd5}
                        </Typography>
                        <Typography variant="body1" sx={{ textAlign: 'center', fontSize: '0.75rem', fontWeight: 'light', lineHeight: 1.3 }}>
                          Base {displayThreshold(5)}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Chip
                      label={useInterpolation ? "Interpolated" : "From Selection"}
                      size="small"
                      color="info"
                      sx={{ height: 18, fontSize: '0.65rem', mt: 0.5 }}
                    />
                  </Box>
                </Box>

                {/* Crop Recommendations */}
                <Box sx={{ my: 1 }}>
                  <Typography variant='subtitle1'>
                    Thermal Potential
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: 'italic', lineHeight: 1.5 }}
                  >
                    Typical crops found in this climate include:{' '}
                    <strong>{getCropRecommendations(climateStats.growthPotential)?.crops.join(', ')}</strong>
                  </Typography>
                </Box>

                <TpTooltip
                  title={
                    <Stack>
                      <Typography>
                        Overall growing capacity (0-100) based on global heat accumulation. Higher scores support more diverse crop varieties
                      </Typography>
                      <Box>
                        <Chip
                          label={useInterpolation ? "Interpolated" : "From Selection"}
                          size="small"
                          color="info"
                          sx={{ height: 18, fontSize: '0.65rem', mt: 0.5, width: 'fit-content' }}
                        />
                      </Box>
                    </Stack>
                  }
                >
                  <Stack sx={{ cursor: 'help' }}>
                    <Box sx={{ height: 8, width: '100%', bgcolor: 'grey.200', borderRadius: 1 }}>
                      <Box
                        sx={{
                          height: '100%',
                          width: `${climateStats.growthPotential}%`,
                          bgcolor: climateStats.color,
                          borderRadius: 1,
                          transition: 'width 0.3s ease'
                        }}
                      />
                    </Box>
                  </Stack>
                </TpTooltip>
              </Box>
            </Stack>
          )}

        </Stack>
      </Stack>

      {/* Action Buttons */}
      <Box
        sx={{
          mt: 1,
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          alignItems: 'stretch', // ensures children stretch to fill container
          height: 'auto', // allow box height to fit content
          minHeight: 'unset', // allow height to shrink if needed
        }}
      >
        <Button
          variant='outlined'
          size='large'
          onClick={_onBack}
          sx={{ height: 'auto', minHeight: 'unset', alignSelf: 'auto' }}
        >
          Back
        </Button>
        <Button
          size='large'
          onClick={_onSubmit}
          disabled={useInterpolation ? !interpolatedData : !selectedHex}
          sx={{ height: 'auto', minHeight: 'unset', alignSelf: 'auto' }}
        >
          Create {name}
        </Button>
      </Box>
    </Box>
  )
}
